import { Align, Button, Dialog, Icon, Row, Tray } from '@busybusy/webapp-react-ui';
import { BusyBusyIcon, CloseIcon } from 'assets/icons';
import classNames from 'classnames';
import Graylog from 'components/domain/analytics/Graylog/Graylog';
import ConditionalContainer from 'components/foundation/ConditionalContainer/ConditionalContainer';
import Thumbnail from 'components/foundation/Thumbnail/Thumbnail';
import IconButton from 'components/foundation/buttons/IconButton/IconButton';
import ConfirmationDialog from 'components/foundation/dialogs/ConfirmationDialog/ConfirmationDialog';
import HeaderDialog from 'components/foundation/dialogs/HeaderDialog/HeaderDialog';
import BulletinEnableForm from 'containers/bulletin/BulletinEnableForm/BulletinEnableForm';
import DailyProjectReportEnableForm from 'containers/daily-project-report/DailyProjectReportEnableForm/DailyProjectReportEnableForm';
import WistiaVideoEmbedDialog from 'components/foundation/WistiaVideoEmbedDialog/WistiaVideoEmbedDialog';
import { DashboardTheme } from 'containers/dashboard/types/types';
import DocumentEnableForm from 'containers/documents/DocumentEnableForm/DocumentEnableForm';
import ProgressTrackingEnableForm from 'containers/progress-report/ProgressTrackingEnableForm/ProgressTrackingEnableForm';
import LaborBudgetsForm from 'containers/settings/budgeting/forms/LaborBudgetsForm';
import { useActiveMember, useOpenable, useOrganization } from 'hooks';
import useMemberUiSettingsUpdate from 'hooks/models/member-settings/useMemberUiSettingsUpdate';
import useMemberSettings from 'hooks/models/member/useMemberSettings';
import { useOrganizationSettings } from 'hooks/models/organization/settings/useOrganizationSettings/useOrganizationSettings';
import useIsInIframe from 'hooks/store/useIsInIframe';
import useOnMount from 'hooks/utils/useOnMount/useOnMount';
import { first, isNil } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { IStoreEnvironment } from 'store/types';
import { ClassName } from 'types/ClassName';
import AnalyticsEvent from 'utils/constants/analytics/analyticsEvent';
import { useFeatureFlags } from 'utils/features';
import LocalStore from 'utils/localStorageUtils';
import { t } from 'utils/localize';
import { splitStringAtSecondChar } from 'utils/stringUtils';
import { EnablePremiumDialog } from '../EnablePremiumDialog/EnablePremiumDialog';
import CertificationsConfirmationForm from './CertificationsConfirmationForm/CertificationsConfirmationForm';
import CertificationsEnableForm from './CertificationsEnableForm/CertificationsEnableForm';
import './DashboardAnnouncement.scss';
import { SafetyReportsConfirmationForm } from './SafetyReportsConfirmationForm/SafetyReportsConfirmationForm';
import SafetyReportsEnableForm from './SafetyReportsEnableForm/SafetyReportsEnableForm';
import { VideoThumbnail_2026_4 } from 'assets/images';
import { WistiaVideoId } from 'components/foundation/WistiaVideoEmbedDialog/constants';
import EquipmentTrackingEnableForm from './EquipmentTrackingEnableForm/EquipmentTrackingEnableForm';
import EquipmentTrackingConfirmationForm from './EquipmentTrackingConfirmationForm/EquipmentTrackingConfirmationForm';

export interface IDashboardAnnouncementProps {
  className?: ClassName;
  theme: DashboardTheme;
}

export const latestAnnouncementVersion = '2026.4';
export const latestAnnouncementKey = `announcement-${latestAnnouncementVersion}-content`;

const DashboardAnnouncement = (props: IDashboardAnnouncementProps) => {
  const { className, theme } = props;
  const isInIframe = useIsInIframe();

  const environment = useSelector((state: { environment: IStoreEnvironment }) => state.environment);

  const classes = classNames(
    'dashboard-announcement',
    theme,
    'mt-6',
    'px-8',
    'pt-4',
    'pb-4',
    { 'is-in-iframe': isInIframe },
    className
  );
  const memberSettings = useMemberSettings();
  const organizationSettings = useOrganizationSettings();
  const organization = useOrganization();

  const member = useActiveMember();
  const isPro = useFeatureFlags('PRO');
  const isPremium = useFeatureFlags('PREMIUM');
  const hasDocumentsAccess = useFeatureFlags('DOCUMENTS');
  const hasBulletinAccess = useFeatureFlags('BULLETIN_FEATURE');
  const hasDailyProjectReportAccess = useFeatureFlags('DAILY_REPORTS');
  const hasProgressTrackingAccess = useFeatureFlags('WORK_ACCOMPLISHED');
  const hasSafetyReportsAccess = useFeatureFlags('SAFETY_INCIDENTS');
  const hasCertificationsAccess = useFeatureFlags('CERTIFICATIONS');
  const hasEquipmentTrackingAccess = useFeatureFlags('EQUIPMENT_GPS_HARDWARE');
  const [showDocumentsAction, setShowDocumentsAction] = useState(false);
  const [showBulletinAction, setShowBulletinAction] = useState(false);
  const [showDailyProjectReportAction, setShowDailyProjectReportAction] = useState(false);
  const [showProgressTrackingAction, setShowProgressTrackingAction] = useState(false);
  const [showSafetyReportsAction, setShowSafetyReportsAction] = useState(false);
  const [showCertificationAction, setShowCertificationsAction] = useState(false);
  const [showEquipmentTrackingAction, setShowEquipmentTrackingAction] = useState(false);
  const updateUiSettings = useMemberUiSettingsUpdate();
  const memberSettingsAnnouncementVersion = memberSettings?.web?.ui?.hideAnnouncementTextForVersion;
  const baseVersion = first(splitStringAtSecondChar(environment.REACT_APP_VERSION, '.'));
  const showAnnouncement =
    memberSettingsAnnouncementVersion !== latestAnnouncementVersion &&
    !hasEquipmentTrackingAccess &&
    organizationSettings?.web?.ui?.requestedEquipmentTrackingAccess !== true; // remove extra checks on next version

  const canManageCompanySettings = member.position?.manageCompanySettings ?? false;

  const documentDialogOpenable = useOpenable();
  const documentRequestCompleteOpenable = useOpenable();
  const bulletinDialogOpenable = useOpenable();
  const bulletinRequestCompleteOpenable = useOpenable();
  const dailyProjectReportDialogOpenable = useOpenable();
  const dailyProjectReportRequestCompleteOpenable = useOpenable();
  const progressTrackingDialogOpenable = useOpenable();
  const progressTrackingRequestCompleteOpenable = useOpenable();
  const safetyReportsDialogOpenable = useOpenable();
  const safetyReportsRequestCompleteOpenable = useOpenable();
  const certificationDialogOpenable = useOpenable();
  const certificationsRequestCompleteOpenable = useOpenable();
  const equipmentTrackingDialogOpenable = useOpenable();
  const equipmentTrackingRequestCompleteOpenable = useOpenable();
  const premiumDialogOpenable = useOpenable();
  const thumbnailVideoOpenable = useOpenable();
  const enableBudgetDialogOpenable = useOpenable();

  function graylogEnableBulletin() {
    Graylog({
      shortMsg: AnalyticsEvent.announcementText.UPGRADE_REQUESTED,
      logObject: {
        _identifier: latestAnnouncementVersion,
        _member_id: member.id,
        _type: 'Enable Bulletin',
      },
    });
  }

  function graylogEnableDocuments() {
    Graylog({
      shortMsg: AnalyticsEvent.announcementText.UPGRADE_REQUESTED,
      logObject: {
        _identifier: latestAnnouncementVersion,
        _member_id: member.id,
        _type: 'Enable Documents',
      },
    });
  }

  function graylogEnableDailyProjectReports() {
    Graylog({
      shortMsg: AnalyticsEvent.announcementText.UPGRADE_REQUESTED,
      logObject: {
        _identifier: latestAnnouncementVersion,
        _member_id: member.id,
        _type: 'Enable Field Reports',
      },
    });
  }

  function graylogEnableProgressTracking() {
    Graylog({
      shortMsg: AnalyticsEvent.announcementText.UPGRADE_REQUESTED,
      logObject: {
        _identifier: latestAnnouncementVersion,
        _member_id: member.id,
        _type: 'Enable Progress Tracking',
      },
    });
  }

  function graylogEnableSafetyReports() {
    Graylog({
      shortMsg: AnalyticsEvent.announcementText.UPGRADE_REQUESTED,
      logObject: {
        _identifier: latestAnnouncementVersion,
        _member_id: member.id,
        _type: 'Enable Safety',
      },
    });
  }

  function graylogUpgradePremium() {
    Graylog({
      shortMsg: AnalyticsEvent.announcementText.UPGRADE_REQUESTED,
      logObject: {
        _identifier: latestAnnouncementVersion,
        _member_id: member.id,
        _type: 'Upgrade Premium',
      },
    });
  }

  const onClose = () => {
    updateUiSettings('hideAnnouncementTextForVersion', latestAnnouncementVersion);
  };

  useOnMount(() => {
    if (
      latestAnnouncementVersion !== memberSettingsAnnouncementVersion &&
      !isNil(LocalStore.get(latestAnnouncementKey)) &&
      LocalStore.get(latestAnnouncementKey) === true
    ) {
      updateUiSettings('hideAnnouncementTextForVersion', latestAnnouncementVersion);
    }
  });

  useEffect(() => {
    setShowDocumentsAction(!hasDocumentsAccess && organizationSettings?.web?.ui?.requestedDocumentsAccess !== true);
  }, [hasDocumentsAccess, organizationSettings]);
  useEffect(() => {
    setShowBulletinAction(!hasBulletinAccess && organizationSettings?.web?.ui?.requestedBulletinAccess !== true);
  }, [hasBulletinAccess, organizationSettings]);
  useEffect(() => {
    setShowDailyProjectReportAction(
      !hasDailyProjectReportAccess && organizationSettings?.web?.ui?.requestedDailyProjectReportsAccess !== true
    );
  }, [hasDailyProjectReportAccess, organizationSettings]);
  useEffect(() => {
    setShowProgressTrackingAction(
      !hasProgressTrackingAccess && organizationSettings?.web?.ui?.requestedProgressTrackingAccess !== true
    );
  }, [hasProgressTrackingAccess, organizationSettings]);
  useEffect(() => {
    setShowSafetyReportsAction(
      !hasSafetyReportsAccess && organizationSettings?.web?.ui?.requestedSafetyReportsAccess !== true
    );
  }, [hasSafetyReportsAccess, organizationSettings]);
  useEffect(() => {
    setShowCertificationsAction(
      !hasCertificationsAccess && organizationSettings?.web?.ui?.requestedCertificationsAccess !== true
    );
  }, [hasCertificationsAccess, organizationSettings]);
  useEffect(() => {
    setShowEquipmentTrackingAction(
      !hasEquipmentTrackingAccess && organizationSettings?.web?.ui?.requestedEquipmentTrackingAccess !== true
    );
  }, [hasEquipmentTrackingAccess, organizationSettings]);

  const onDocumentRequestSubmitted = () => {
    setShowDocumentsAction(false);
    documentRequestCompleteOpenable.open();
    documentDialogOpenable.close();
  };

  const onBulletinRequestSubmitted = () => {
    setShowBulletinAction(false);
    bulletinRequestCompleteOpenable.open();
    bulletinDialogOpenable.close();
  };

  const onDailyProjectReportRequestSubmitted = () => {
    setShowDailyProjectReportAction(false);
    dailyProjectReportRequestCompleteOpenable.open();
    dailyProjectReportDialogOpenable.close();
  };

  const onProgressTrackingRequestSubmitted = () => {
    setShowProgressTrackingAction(false);
    progressTrackingRequestCompleteOpenable.open();
    progressTrackingDialogOpenable.close();
  };

  const onSafetyReportsRequestSubmitted = () => {
    setShowSafetyReportsAction(false);
    safetyReportsRequestCompleteOpenable.open();
    safetyReportsDialogOpenable.close();
  };

  const onCertificationsRequestSubmitted = () => {
    setShowCertificationsAction(false);
    certificationsRequestCompleteOpenable.open();
    certificationDialogOpenable.close();
  };

  const onEquipmentTrackingRequestSubmitted = () => {
    setShowEquipmentTrackingAction(false);
    equipmentTrackingRequestCompleteOpenable.open();
    equipmentTrackingDialogOpenable.close();
  };

  const renderTitle = () => (
    <Row align={Align.CENTER}>
      <p className="title">{t(`New Release | ${latestAnnouncementVersion}`)}</p>
    </Row>
  );

  const renderIcon = () => <Icon className="announcement-icon" svg={BusyBusyIcon} />;

  const renderVideoDetails = () => (
    <div className="pb-5">
      <Thumbnail className={'my-2'} fileUrl={VideoThumbnail_2026_4} onClick={thumbnailVideoOpenable.open} />
    </div>
  );

  const renderList = () => (
    <div className="list-content mx-10 mt-2">
      <ConditionalContainer
        condition={
          canManageCompanySettings &&
          isPro &&
          !isPremium &&
          (showDocumentsAction ||
            showDailyProjectReportAction ||
            showSafetyReportsAction ||
            showBulletinAction ||
            showProgressTrackingAction ||
            showCertificationAction ||
            showEquipmentTrackingAction)
        }
      >
        <div className="content-title">{t('Available Upgrades')}</div>
        <Tray>
          <ul className="pl-4">
            <li>
              <a
                className="link ml-1"
                onClick={() => {
                  graylogUpgradePremium();
                  premiumDialogOpenable.open();
                }}
              >
                {t('Premium')}
              </a>
            </li>
            {showDocumentsAction && (
              <li>
                <a
                  className="link ml-1"
                  onClick={() => {
                    graylogEnableDocuments();
                    documentDialogOpenable.open();
                  }}
                >
                  {t('Documents')}
                </a>
              </li>
            )}
            {showDailyProjectReportAction && (
              <li>
                <a
                  className="link ml-1"
                  onClick={() => {
                    graylogEnableDailyProjectReports();
                    dailyProjectReportDialogOpenable.open();
                  }}
                >
                  {'Field Reports'}
                </a>
              </li>
            )}
            {showBulletinAction && (
              <li>
                <a
                  className="link ml-1"
                  onClick={() => {
                    graylogEnableBulletin();
                    bulletinDialogOpenable.open();
                  }}
                >
                  {'Bulletin'}
                </a>
              </li>
            )}
            {showProgressTrackingAction && (
              <li>
                <a
                  className="link ml-1"
                  onClick={() => {
                    graylogEnableProgressTracking();
                    progressTrackingDialogOpenable.open();
                  }}
                >
                  {'Progress Tracking'}
                </a>
              </li>
            )}
            {showSafetyReportsAction && (
              <li>
                <a
                  className="link ml-1"
                  onClick={() => {
                    graylogEnableSafetyReports();
                    safetyReportsDialogOpenable.open();
                  }}
                >
                  {'Safety'}
                </a>
              </li>
            )}
            {showCertificationAction && (
              <li>
                <a className="link ml-1" onClick={certificationDialogOpenable.open}>
                  {t('Operations')}
                </a>
              </li>
            )}
            {showEquipmentTrackingAction && (
              <li>
                <a className="link ml-1" onClick={equipmentTrackingDialogOpenable.open}>
                  {t('GPS Equipment Tracking')}
                </a>
              </li>
            )}
          </ul>
        </Tray>
      </ConditionalContainer>
    </div>
  );

  const renderCurrentReleaseDetails = () => (
    <div className="release-details ml-10">
      <div className="content-title mb-4">{t(`Tired of Not Knowing Where Your Equipment Is?`)}</div>
      <div className="content mb-4">
        {t(
          `BusyBusy GPS Equipment Tracking helps contractors spend less time searching for equipment and more time getting work done. See where machines are located, where they’ve been, and how they’re being used across jobsites—all directly inside BusyBusy. \n\n`
        )}
        {t(
          `Access near real-time GPS location, breadcrumbing, movement history, and telematics data like engine status, RPM, fuel level, and more to improve visibility and equipment usage. \n\n`
        )}
        {t(
          `Hundreds of companies have already requested more information to see how our GPS Equipment Tracking can help their crews spend less time tracking down equipment and more time staying productive. It's time to see it for yourself! \n\n`
        )}
        <ConditionalContainer
          condition={
            !hasEquipmentTrackingAccess && organizationSettings?.web?.ui?.requestedEquipmentTrackingAccess !== true
          }
        >
          <Button type="primary" onClick={equipmentTrackingDialogOpenable.open}>
            {t('Learn More')}
          </Button>
        </ConditionalContainer>
      </div>
    </div>
  );

  const renderContent = () => {
    return (
      <Tray className="content-column mt-2">
        <Tray>
          {renderVideoDetails()}
          {renderCurrentReleaseDetails()}
        </Tray>
        {renderList()}
      </Tray>
    );
  };

  const renderAnnouncement = () => {
    return (
      <>
        <div className={classes}>
          <IconButton className="close-icon" svg={CloseIcon} onClick={onClose} />
          <Row align={Align.CENTER} className={theme}>
            {renderIcon()}
            {renderTitle()}
          </Row>
          {renderContent()}
        </div>
        <HeaderDialog
          isOpen={documentDialogOpenable.isOpen}
          onClose={documentDialogOpenable.close}
          divider={false}
          title={t('Documents')}
        >
          <DocumentEnableForm className="px-8 pb-9" onRequestSubmitted={onDocumentRequestSubmitted} />
        </HeaderDialog>
        <ConfirmationDialog
          title={t('Submitted')}
          message={t(
            'Your request has been submitted and Documents will soon be enabled for your account. We’ll send you a message letting you know when the feature is ready to use. Thank you!'
          )}
          isOpen={documentRequestCompleteOpenable.isOpen}
          confirmButtonText={t('OK')}
          onConfirm={documentRequestCompleteOpenable.close}
        />
        <HeaderDialog
          isOpen={bulletinDialogOpenable.isOpen}
          onClose={bulletinDialogOpenable.close}
          divider={false}
          title={t('Bulletin')}
        >
          <BulletinEnableForm className="px-8 pb-9" onRequestSubmitted={onBulletinRequestSubmitted} />
        </HeaderDialog>
        <ConfirmationDialog
          title={t('Submitted')}
          message={t(
            'Your request has been submitted and Bulletin will soon be enabled for your account. We’ll send you a message letting you know when the feature is ready to use. Thank you!'
          )}
          isOpen={bulletinRequestCompleteOpenable.isOpen}
          confirmButtonText={t('OK')}
          onConfirm={bulletinRequestCompleteOpenable.close}
        />
        <HeaderDialog
          isOpen={dailyProjectReportDialogOpenable.isOpen}
          onClose={dailyProjectReportDialogOpenable.close}
          divider={false}
          title={t('Field Reports')}
        >
          <DailyProjectReportEnableForm
            className="px-8 pb-9"
            onRequestSubmitted={onDailyProjectReportRequestSubmitted}
          />
        </HeaderDialog>
        <ConfirmationDialog
          title={t('Submitted')}
          message={t(
            'Your request has been submitted and Field Reports will soon be enabled for your account. We’ll send you a message letting you know when the feature is ready to use. Thank you!'
          )}
          isOpen={dailyProjectReportRequestCompleteOpenable.isOpen}
          confirmButtonText={t('OK')}
          onConfirm={dailyProjectReportRequestCompleteOpenable.close}
        />
        <HeaderDialog
          isOpen={progressTrackingDialogOpenable.isOpen}
          onClose={progressTrackingDialogOpenable.close}
          divider={false}
          title={t('Progress Tracking')}
        >
          <ProgressTrackingEnableForm className="px-8 pb-9" onRequestSubmitted={onProgressTrackingRequestSubmitted} />
        </HeaderDialog>
        <ConfirmationDialog
          title={t('Submitted')}
          message={t(
            'Your request has been submitted and Progress Tracking will soon be enabled for your account. We’ll send you a message letting you know when the feature is ready to use. Thank you!'
          )}
          isOpen={progressTrackingRequestCompleteOpenable.isOpen}
          confirmButtonText={t('OK')}
          onConfirm={progressTrackingRequestCompleteOpenable.close}
        />
        <HeaderDialog
          isOpen={safetyReportsDialogOpenable.isOpen}
          onClose={safetyReportsDialogOpenable.close}
          divider={false}
          title={t('Safety')}
        >
          <SafetyReportsEnableForm className="px-8 pb-9" onRequestSubmitted={onSafetyReportsRequestSubmitted} />
        </HeaderDialog>
        <HeaderDialog
          isOpen={safetyReportsRequestCompleteOpenable.isOpen}
          onClose={safetyReportsRequestCompleteOpenable.close}
          divider={false}
          title={'Submitted'}
        >
          <SafetyReportsConfirmationForm onConfirm={safetyReportsRequestCompleteOpenable.close} />
        </HeaderDialog>

        <HeaderDialog
          isOpen={certificationDialogOpenable.isOpen}
          onClose={certificationDialogOpenable.close}
          divider={false}
          title={t('Operations Module')}
        >
          <CertificationsEnableForm className="px-8 pb-9" onRequestSubmitted={onCertificationsRequestSubmitted} />
        </HeaderDialog>
        <HeaderDialog
          isOpen={certificationsRequestCompleteOpenable.isOpen}
          onClose={certificationsRequestCompleteOpenable.close}
          divider={false}
          title={'Submitted'}
        >
          <CertificationsConfirmationForm onConfirm={certificationsRequestCompleteOpenable.close} />
        </HeaderDialog>

        <HeaderDialog
          isOpen={equipmentTrackingDialogOpenable.isOpen}
          onClose={equipmentTrackingDialogOpenable.close}
          divider={false}
          title={t('GPS Equipment Tracking')}
        >
          <EquipmentTrackingEnableForm className="px-8 pb-9" onRequestSubmitted={onEquipmentTrackingRequestSubmitted} />
        </HeaderDialog>
        <HeaderDialog
          isOpen={equipmentTrackingRequestCompleteOpenable.isOpen}
          onClose={equipmentTrackingRequestCompleteOpenable.close}
          divider={false}
          title={t('Submitted')}
        >
          <EquipmentTrackingConfirmationForm onConfirm={equipmentTrackingRequestCompleteOpenable.close} />
        </HeaderDialog>

        <HeaderDialog
          isOpen={premiumDialogOpenable.isOpen}
          onClose={premiumDialogOpenable.close}
          title={t('Available Services')}
          size={'full'}
        >
          <EnablePremiumDialog />
        </HeaderDialog>

        <WistiaVideoEmbedDialog
          isOpen={thumbnailVideoOpenable.isOpen}
          onClose={thumbnailVideoOpenable.close}
          videoId={WistiaVideoId.DASHBOARD_ANNOUNCEMENT_TEXT}
        />
        <Dialog isOpen={enableBudgetDialogOpenable.isOpen} onClose={enableBudgetDialogOpenable.close} position="center">
          <LaborBudgetsForm
            className="p-8"
            trackBudgets={organization.trackBudgets ?? false}
            trackBudgetCost={organization.trackBudgetCost ?? false}
            trackBudgetEquipmentHours={organization.trackBudgetEquipmentHours ?? false}
            trackBudgetEquipmentCosts={organization.trackBudgetEquipmentCosts ?? false}
            trackBudgetProgress={organization.trackBudgetProgress ?? false}
            trackBudgetProgressValue={organization.trackBudgetProgressValue ?? false}
            trackCostCode={organization.trackCostCode ?? false}
            trackEquipment={organization.trackEquipment ?? false}
            performanceScores={organization.performanceScores ?? false}
            onSubmit={enableBudgetDialogOpenable.close}
          />
        </Dialog>
      </>
    );
  };

  return showAnnouncement ? renderAnnouncement() : <></>;
};

export default DashboardAnnouncement;
